<template>
  <div>
    <!-- Single item -->
    <div v-if="items.length === 1" class="w-full flex justify-center">
      <div
        class="relative overflow-hidden rounded-lg"
        :class="[
          // Use full width for images, constrained width for reels
          items[0].type === 'video' ? 'w-[min(540px,100%)]' : 'w-full max-w-3xl',
        ]"
      >
        <MediaItem
          :item="items[0]"
          :index="0"
          :is-playing="isPlaying[0]"
          :aspect-ratio="getAspectRatio(items[0])"
          :preserve-ratio="true"
          :is-single-item="true"
          :bubble-id="bubbleId"
          class="rounded-lg overflow-hidden"
          @click="openGallery(0)"
          @play="handlePlay(0)"
        />
      </div>
    </div>

    <!-- Two items -->
    <div v-else-if="items.length === 2" class="grid grid-cols-2 gap-1">
      <MediaItem
        v-for="(item, index) in items"
        :key="index"
        :item="item"
        :index="index"
        :is-playing="isPlaying[index]"
        :aspect-ratio="getAspectRatio(item)"
        :is-single-item="false"
        :bubble-id="bubbleId"
        @click="openGallery(index)"
        @play="handlePlay(index)"
      />
    </div>

    <!-- Three items -->
    <div v-else-if="items.length === 3" class="grid grid-cols-2 gap-1">
      <div class="col-span-2">
        <MediaItem
          :item="items[0]"
          :index="0"
          :is-playing="isPlaying[0]"
          :aspect-ratio="getAspectRatio(items[0])"
          :is-single-item="false"
          :bubble-id="bubbleId"
          @click="openGallery(0)"
          @play="handlePlay(0)"
        />
      </div>
      <MediaItem
        v-for="(item, index) in items.slice(1)"
        :key="index + 1"
        :item="item"
        :index="index + 1"
        :is-playing="isPlaying[index + 1]"
        :aspect-ratio="getAspectRatio(item)"
        :is-single-item="false"
        :bubble-id="bubbleId"
        @click="openGallery(index + 1)"
        @play="handlePlay(index + 1)"
      />
    </div>

    <!-- Four or more items -->
    <div v-else class="grid grid-cols-2 gap-1">
      <MediaItem
        v-for="(item, index) in items.slice(0, 4)"
        :key="index"
        :item="item"
        :index="index"
        :is-playing="isPlaying[index]"
        :aspect-ratio="getAspectRatio(item)"
        :is-single-item="false"
        :bubble-id="bubbleId"
        @click="openGallery(index)"
        @play="handlePlay(index)"
      >
        <div
          v-if="index === 3 && items.length > 4"
          class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white rounded-lg cursor-pointer"
          @click.stop="openGallery(3)"
        >
          <span class="text-2xl font-bold">+{{ items.length - 4 }}</span>
        </div>
      </MediaItem>
    </div>

    <!-- Full-screen gallery -->
    <ImageViewer
      v-if="isGalleryOpen"
      :is-open="isGalleryOpen"
      :media-items="items"
      :initial-index="currentIndex"
      :bubble-id="bubbleId"
      @close="closeGallery"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useLogger } from '@/composables/useLogger'
import MediaItem from './MediaItem.vue'
import ImageViewer from '@/components/ImageViewer.vue'
import { useKeyboardNavigation } from '@/composables/useKeyboardNavigation'

const logger = useLogger()
const { openGallery: setGalleryOpen, closeGallery: setGalleryClosed } = useKeyboardNavigation()

export interface MediaItem {
  type: 'image' | 'video'
  src: string
  cover_photo?: string
  width?: number
  height?: number
}

const props = defineProps<{
  items: MediaItem[]
  bubbleId?: string
}>()

const isPlaying = ref<boolean[]>(new Array(props.items.length).fill(false))
const isGalleryOpen = ref(false)
const currentIndex = ref(0)

const getAspectRatio = (item: MediaItem): string => {
  if (item.width && item.height) {
    // Use original aspect ratio if available
    return `${item.width}/${item.height}`
  }

  // For single reels/videos, use vertical format (Instagram reel ratio)
  if (props.items.length === 1 && item.type === 'video') {
    return '9/16'
  }

  // Default Instagram aspect ratios for other cases
  const ratio = item.width && item.height ? item.width / item.height : 1
  if (ratio > 1.91) return '16/9' // Landscape max
  if (ratio < 0.8) return '4/5' // Portrait max
  return '1/1' // Square default
}

const openGallery = (index: number) => {
  logger.debug('🔍 MediaGallery: Opening gallery', {
    index,
    itemType: props.items[index].type,
    itemSrc: props.items[index].src,
    bubbleId: props.bubbleId,
    totalItems: props.items.length,
  })
  // Pause all videos when opening gallery
  isPlaying.value = isPlaying.value.map(() => false)

  // Find all video elements and pause them
  const videos = document.querySelectorAll('video')
  videos.forEach((video) => {
    video.pause()
  })

  currentIndex.value = index
  isGalleryOpen.value = true
  setGalleryOpen()
}

const closeGallery = () => {
  logger.debug('🎯 MediaGallery: Closing gallery')
  isGalleryOpen.value = false
  setGalleryClosed()

  // Ensure we reset the state after a short delay to allow any pending events to complete
  setTimeout(() => {
    setGalleryClosed()
  }, 100)

  // Pause all videos when closing gallery
  isPlaying.value = isPlaying.value.map(() => false)
}

const handlePlay = (index: number) => {
  logger.debug('🎯 MediaGallery: Video play triggered', {
    index,
    itemType: props.items[index].type,
    itemSrc: props.items[index].src,
  })
  // Pause all other videos
  isPlaying.value = isPlaying.value.map((_, i) => i === index)

  // Find all video elements and pause them except the one that should be playing
  const videos = document.querySelectorAll('video')
  videos.forEach((video, i) => {
    if (i !== index) {
      video.pause()
    }
  })
}

onMounted(() => {
  logger.debug('🔍 MediaGallery: Component mounted', {
    bubbleId: props.bubbleId,
    totalItems: props.items.length,
    itemTypes: props.items.map((item) => item.type),
  })
})
</script>

<style scoped>
.grid-container {
  max-width: 100%;
  margin: 0 auto;
}

.grid {
  display: grid;
  gap: 4px;
}

.row-span-2 {
  grid-row: span 2;
}
</style>
